import { useState, useEffect, useCallback } from "react";
import {
  Globe, Plus, Trash2, RefreshCw, AlertCircle, CheckCircle2,
  Loader2, TrendingUp, Sparkles, ExternalLink,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";

export default function GoogleSEOTab({ api, onAskAgent }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newSite, setNewSite] = useState({ url: "", label: "", client_name: "", keywords: "" });
  const [auditing, setAuditing] = useState(null); // site_id en cours d'audit
  const [selectedAudit, setSelectedAudit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/google/seo/sites");
      setSites(r.data || []);
    } catch (e) {
      toast.error("Erreur chargement sites");
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newSite.url) { toast.error("URL requise"); return; }
    try {
      await api.post("/google/seo/sites", {
        url: newSite.url,
        label: newSite.label,
        client_name: newSite.client_name,
        keywords: newSite.keywords.split(",").map(k => k.trim()).filter(Boolean),
      });
      toast.success("Site ajoute");
      setShowNew(false);
      setNewSite({ url: "", label: "", client_name: "", keywords: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const runAudit = async (site) => {
    setAuditing(site.site_id);
    try {
      const r = await api.post(`/google/seo/sites/${site.site_id}/audit`);
      setSelectedAudit(r.data);
      toast.success(`Audit termine — Score ${r.data.score}/100`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur audit");
    }
    setAuditing(null);
  };

  const handleDelete = async (site_id) => {
    if (!window.confirm("Supprimer ce site et tout son historique d'audit ?")) return;
    await api.delete(`/google/seo/sites/${site_id}`);
    toast.success("Site supprime");
    load();
  };

  const askAgentForAudit = (audit) => {
    onAskAgent({
      domain: "seo",
      url: audit.url,
      seo_audit: `Score: ${audit.score}/100\nIssues: ${(audit.issues || []).map(i => `[${i.severity}] ${i.label}`).join("; ")}\nWins: ${(audit.wins || []).join(", ")}`,
      action: "audit_seo",
    });
  };

  return (
    <div className="space-y-5" data-testid="google-seo-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-white">Sites surveilles</h3>
          <p className="text-xs text-slate-500 mt-0.5">Audit SEO automatique : meta tags, structure HTML, accessibilite, contenu.</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="seo-add-site-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un site
        </Button>
      </div>

      {showNew && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4 space-y-3" data-testid="seo-new-site-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="URL (https://...)" value={newSite.url} onChange={e => setNewSite({ ...newSite, url: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" data-testid="seo-input-url" />
            <Input placeholder="Libelle (ex: Site client X)" value={newSite.label} onChange={e => setNewSite({ ...newSite, label: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Nom du client" value={newSite.client_name} onChange={e => setNewSite({ ...newSite, client_name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
            <Input placeholder="Mots-cles cibles (separes par virgule)" value={newSite.keywords} onChange={e => setNewSite({ ...newSite, keywords: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="seo-confirm-add-site">Ajouter</Button>
            <Button onClick={() => setShowNew(false)} variant="outline" className="border-white/10 text-white">Annuler</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : sites.length === 0 ? (
        <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
          <Globe className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucun site surveille. Ajoutez votre premier site pour lancer un audit SEO.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map(s => (
            <div key={s.site_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/30 transition-all" data-testid={`seo-site-${s.site_id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#0b0811] flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#4285F4]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-white truncate">{s.label || s.domain}</h4>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#f7c935]"><ExternalLink className="w-3 h-3" /></a>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{s.url}</div>
                    {s.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {s.keywords.slice(0, 5).map(k => (
                          <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{k}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {s.last_score != null && (
                    <div className="text-center">
                      <div className={`text-2xl font-black ${s.last_score >= 80 ? "text-emerald-400" : s.last_score >= 60 ? "text-amber-400" : "text-red-400"}`}>{s.last_score}</div>
                      <div className="text-[9px] text-slate-500 uppercase tracking-wider">Score</div>
                    </div>
                  )}
                  <Button onClick={() => runAudit(s)} disabled={auditing === s.site_id} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid={`seo-audit-btn-${s.site_id}`}>
                    {auditing === s.site_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    <span className="ml-1.5">{auditing === s.site_id ? "Audit..." : "Auditer"}</span>
                  </Button>
                  <Button onClick={() => handleDelete(s.site_id)} size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audit detail modal */}
      {selectedAudit && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedAudit(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-[#0b0811] border border-white/10 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" data-testid="seo-audit-modal">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0b0811] z-10">
              <h3 className="text-base font-bold text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#f7c935]" /> Resultat de l'audit</h3>
              <button onClick={() => setSelectedAudit(null)} className="text-slate-400 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-center py-6 bg-gradient-to-br from-[#14101c] to-[#0b0811] rounded-xl border border-white/5">
                <div className={`text-6xl font-black ${selectedAudit.score >= 80 ? "text-emerald-400" : selectedAudit.score >= 60 ? "text-amber-400" : "text-red-400"}`}>{selectedAudit.score}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Score / 100</div>
                <div className="text-[10px] text-slate-600 mt-1">{selectedAudit.url}</div>
              </div>

              <Button onClick={() => askAgentForAudit(selectedAudit)} className="w-full bg-gradient-to-r from-[#4285F4] to-[#EA4335] hover:opacity-90 text-white font-bold" data-testid="seo-ask-agent-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> Demander a l'agent IA des suggestions concretes
              </Button>

              {selectedAudit.wins?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider mb-2">Points forts</div>
                  <ul className="space-y-1">
                    {selectedAudit.wins.map((w, i) => (
                      <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedAudit.issues?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-red-400 tracking-wider mb-2">A corriger ({selectedAudit.issues.length})</div>
                  <ul className="space-y-2">
                    {selectedAudit.issues.map((iss, i) => (
                      <li key={i} className="text-xs text-slate-300 flex items-start gap-2 p-2 rounded-lg bg-[#14101c]" data-testid={`seo-issue-${i}`}>
                        <AlertCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${iss.severity === "high" ? "text-red-400" : iss.severity === "medium" ? "text-amber-400" : "text-slate-400"}`} />
                        <span className="flex-1">{iss.label}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${iss.severity === "high" ? "bg-red-500/15 text-red-300" : iss.severity === "medium" ? "bg-amber-500/15 text-amber-300" : "bg-slate-500/15 text-slate-400"}`}>
                          {iss.severity === "high" ? "HAUTE" : iss.severity === "medium" ? "MOYENNE" : "BASSE"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedAudit.scrape && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-[10px] uppercase font-bold text-slate-400 tracking-wider hover:text-white">Donnees techniques scrappees</summary>
                  <div className="mt-2 grid grid-cols-2 gap-2 p-3 bg-[#14101c] rounded-lg">
                    <div><span className="text-slate-500">Status HTTP:</span> <span className="text-white">{selectedAudit.scrape.status_code}</span></div>
                    <div><span className="text-slate-500">Title len:</span> <span className="text-white">{selectedAudit.scrape.title_length}</span></div>
                    <div><span className="text-slate-500">Meta desc len:</span> <span className="text-white">{selectedAudit.scrape.meta_description_length}</span></div>
                    <div><span className="text-slate-500">H1 / H2 / H3:</span> <span className="text-white">{selectedAudit.scrape.h1_count} / {selectedAudit.scrape.h2_count} / {selectedAudit.scrape.h3_count}</span></div>
                    <div><span className="text-slate-500">Images sans alt:</span> <span className="text-white">{selectedAudit.scrape.img_without_alt} / {selectedAudit.scrape.img_total}</span></div>
                    <div><span className="text-slate-500">Liens int / ext:</span> <span className="text-white">{selectedAudit.scrape.links_internal} / {selectedAudit.scrape.links_external}</span></div>
                    <div><span className="text-slate-500">Mots:</span> <span className="text-white">{selectedAudit.scrape.word_count}</span></div>
                    <div><span className="text-slate-500">Taille HTML:</span> <span className="text-white">{selectedAudit.scrape.html_size_kb} KB</span></div>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
